import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { setDefaultLanguage, onLanguageChange, currentLanguage, setCustomDictionary } from "@/lib/i18n";
import { setFormatDefaults } from "@/lib/chartTheme";

// Item 20: effective regional settings (user > tenant > platform) applied app-wide:
// UI language + RTL, number locale, default currency, Indian/intl grouping, date format, timezone.
export const PLATFORM_REGIONAL = { language: "en", locale: "en-US", currency: "USD", timezone: "UTC", date_format: "yyyy-MM-dd", number_system: "intl", first_day_of_week: 1, fiscal_year_start_month: 1, week_numbering: "iso", rtl: false };

export function applyRegional(reg) {
  const r = { ...PLATFORM_REGIONAL, ...(reg || {}) };
  setFormatDefaults({ locale: r.locale, currency: r.currency, numberSystem: r.number_system, dateFormat: r.date_format, timezone: r.timezone, firstDay: r.first_day_of_week });
  setDefaultLanguage(r.language);
  setCustomDictionary(r.dictionary);
  return r;
}

// dd/MM/yyyy · MM/dd/yyyy · yyyy-MM-dd · dd-MMM-yyyy · dd.MM.yyyy — with optional IANA timezone.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function formatDateRegional(v, pattern, timezone) {
  if (v == null || v === "" || !pattern) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  let y, m, day;
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(String(v));
  if (timezone && !isDateOnly) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(d);
      const g = (t) => parts.find((p) => p.type === t)?.value;
      y = Number(g("year")); m = Number(g("month")); day = Number(g("day"));
    } catch { /* fall through to UTC */ }
  }
  if (y === undefined) { y = d.getUTCFullYear(); m = d.getUTCMonth() + 1; day = d.getUTCDate(); }
  const pad = (n) => String(n).padStart(2, "0");
  return pattern.replace(/yyyy/g, String(y)).replace(/MMM/g, MONTHS[m - 1]).replace(/MM/g, pad(m)).replace(/dd/g, pad(day));
}

const RegionalContext = createContext({ regional: PLATFORM_REGIONAL, presets: null, reload: () => {}, lang: "en" });

export function RegionalProvider({ children }) {
  const { user } = useAuth();
  const [regional, setRegional] = useState(PLATFORM_REGIONAL);
  const [lang, setLang] = useState(currentLanguage());
  useEffect(() => onLanguageChange(setLang), []);
  const [siteId, setSiteId] = useState("");
  const reload = useCallback(async (site) => {
    if (!user) { setRegional(applyRegional(null)); return; }
    const sid = site === undefined ? siteId : site;
    try {
      const { data } = await api.get("/regional/effective", { params: sid ? { site_id: sid } : {} });
      setRegional(applyRegional(data));
    } catch { setRegional(applyRegional(null)); }
  }, [user, siteId]);
  useEffect(() => { reload(); }, [reload]);
  // Item 20: a dashboard's workspace (site) layer sits between the tenant default and the user override
  const setSiteScope = useCallback((sid) => { setSiteId(sid || ""); }, []);
  const value = useMemo(() => ({ regional, reload, lang, setSiteScope, siteId }), [regional, reload, lang, setSiteScope, siteId]);
  return <RegionalContext.Provider value={value}>{children}</RegionalContext.Provider>;
}

export const useRegional = () => useContext(RegionalContext);
